import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiService } from './base-api/api.service';
import {
  BulkClaimDecisionResult,
  ClaimDecisionReasonCode,
  ClaimDecisionRequest,
  ClaimDetail,
  ClaimDocumentInput,
  ClaimStatus,
  ClaimSummary,
  CreateClaim,
  MarkClaimPaid,
  ResubmitClaim,
} from '../models/expense-claim';

/**
 * Student expense claims and the reviewer/finance queue.
 *
 * Students upload supporting documents first, then reference the returned object
 * keys when submitting (or resubmitting) a claim.
 */
@Injectable({ providedIn: 'root' })
export class ExpenseClaimApiService {
  private readonly api = inject(ApiService);

  // ---- Student -----------------------------------------------------------

  getMyClaims(): Observable<ClaimSummary[]> {
    return this.api
      .get<{ data: ClaimSummary[] }>('claims/my-claims')
      .pipe(map((response) => response.data));
  }

  getMyClaim(id: number): Observable<ClaimDetail> {
    return this.api
      .get<{ data: ClaimDetail }>(`claims/my-claims/${id}`)
      .pipe(map((response) => response.data));
  }

  submitClaim(request: CreateClaim): Observable<ClaimDetail> {
    return this.api
      .post<{ data: ClaimDetail }>('claims', request)
      .pipe(map((response) => response.data));
  }

  /** Responds to a rejection with a corrected amount and/or fresh documents. */
  resubmitClaim(id: number, request: ResubmitClaim): Observable<ClaimDetail> {
    return this.api
      .post<{ data: ClaimDetail }>(`claims/${id}/resubmit`, request)
      .pipe(map((response) => response.data));
  }

  cancelClaim(id: number): Observable<unknown> {
    return this.api.post(`claims/${id}/cancel`, {});
  }

  /**
   * Uploads a supporting document and returns the object key to attach.
   *
   * The API returns the stored size as `size`; the claim DTO expects `fileSize`.
   */
  uploadDocument(file: File): Observable<ClaimDocumentInput> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    interface UploadResponse {
      objectKey: string;
      fileName: string;
      size: number;
      contentType?: string | null;
    }

    return this.api
      .post<{ data: UploadResponse }>('claims/documents', formData)
      .pipe(
        map((response) => ({
          objectKey: response.data.objectKey,
          fileName: response.data.fileName,
          contentType: response.data.contentType ?? null,
          fileSize: response.data.size ?? 0,
        }))
      );
  }

  downloadMyDocument(claimId: number, documentId: number): Observable<Blob> {
    return this.api.get<Blob>(`claims/my-claims/${claimId}/documents/${documentId}/download`, {
      responseType: 'blob',
    });
  }

  // ---- Reviewer / finance ------------------------------------------------

  getClaimsForReview(
    status?: ClaimStatus | null,
    scholarshipId?: number | null,
    scholarshipAwardId?: number | null,
    search?: string
  ): Observable<ClaimSummary[]> {
    const params = new URLSearchParams();
    if (status) {
      params.set('status', status);
    }
    if (scholarshipId) {
      params.set('scholarshipId', String(scholarshipId));
    }
    if (scholarshipAwardId) {
      params.set('scholarshipAwardId', String(scholarshipAwardId));
    }
    if (search) {
      params.set('search', search);
    }
    const query = params.toString();

    return this.api
      .get<{ data: ClaimSummary[] }>(`admin/claims${query ? `?${query}` : ''}`)
      .pipe(map((response) => response.data));
  }

  getClaimForReview(id: number): Observable<ClaimDetail> {
    return this.api
      .get<{ data: ClaimDetail }>(`admin/claims/${id}`)
      .pipe(map((response) => response.data));
  }

  decide(id: number, decision: ClaimDecisionRequest): Observable<ClaimDetail> {
    return this.api
      .put<{ data: ClaimDetail }>(`admin/claims/${id}/decision`, decision)
      .pipe(map((response) => response.data));
  }

  /** Approve or reject many claims at once, with a shared reason. */
  bulkDecide(ids: number[], decision: ClaimDecisionRequest): Observable<BulkClaimDecisionResult> {
    return this.api
      .post<{ data: BulkClaimDecisionResult }>('admin/claims/bulk-decision', {
        ids,
        decision: decision.decision,
        reasonCode: decision.reasonCode ?? null,
        reason: decision.reason ?? null,
      })
      .pipe(map((response) => response.data));
  }

  markPaid(id: number, request: MarkClaimPaid): Observable<ClaimDetail> {
    return this.api
      .post<{ data: ClaimDetail }>(`admin/claims/${id}/paid`, request)
      .pipe(map((response) => response.data));
  }

  reviewDocument(
    claimId: number,
    documentId: number,
    decision: 'Approved' | 'Rejected',
    notes?: string | null
  ): Observable<unknown> {
    return this.api.put(`admin/claims/${claimId}/documents/${documentId}/review`, { decision, notes });
  }

  downloadDocument(claimId: number, documentId: number): Observable<Blob> {
    return this.api.get<Blob>(`admin/claims/${claimId}/documents/${documentId}/download`, {
      responseType: 'blob',
    });
  }

  /** Human-readable labels for the rejection reason codes. */
  static readonly reasonLabels: Record<ClaimDecisionReasonCode, string> = {
    CLEARER_DOCUMENT_REQUIRED: 'A clearer document is required',
    INCORRECT_AMOUNT: 'Incorrect amount',
    NOT_ELIGIBLE: 'Not covered by the awarded benefits',
    DUPLICATE_CLAIM: 'Duplicate claim',
    INSUFFICIENT_BUDGET: 'Insufficient budget remaining',
    INCOMPLETE_DETAILS: 'Incomplete details',
    OTHER: 'Other',
  };
}
